import { supabase } from '../supabaseClient';
import { readFunctionError } from '../utils/functionError';
import type { StaffRole } from './staffService';

/**
 * The invitee's side of a staff invite: everything the /staff-invite page
 * needs, from reading the emailed link to submitting a guard's licence.
 * The workspace side (sending and resending) lives in staffService.
 */

export type StaffInviteFailure = 'invalid' | 'accepted' | 'revoked' | 'expired';

export interface StaffInviteDetails {
  email: string;
  first_name: string;
  last_name: string;
  role: StaffRole;
  /** Will work shifts, so a phone number and a licence are asked for. */
  is_guard: boolean;
  company_name: string | null;
  expires_at: string | null;
}

export interface AcceptedStaffInvite {
  email: string;
  role: StaffRole;
  is_guard: boolean;
}

export interface AcceptStaffInviteParams {
  token: string;
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
}

export interface LicenceTypeOption {
  id: string;
  name: string;
  authority: string;
  country_code: string;
}

export interface SubmitLicenceParams {
  holderName: string;
  licenceNumber: string;
  licenceTypeId: string;
  issueDate: string | null;
  expiryDate: string;
  document: File;
  notes: string | null;
}

/** Why a link cannot be used, so the page can say something more useful than "invalid". */
export class StaffInviteError extends Error {
  reason: StaffInviteFailure;

  constructor(reason: StaffInviteFailure, message?: string) {
    super(message ?? reason);
    this.name = 'StaffInviteError';
    this.reason = reason;
  }
}

export const LICENCE_DOCUMENT_BUCKET = 'licence-documents';
export const LICENCE_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
/** What the licence review screen can display. */
export const LICENCE_DOCUMENT_TYPES: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
};

const toFailure = (value: unknown): StaffInviteFailure => (
  value === 'accepted' || value === 'revoked' || value === 'expired' ? value : 'invalid'
);

const toRole = (value: unknown): StaffRole => (
  value === 'admin' || value === 'manager' ? value : 'guard'
);

/** profiles.phone is numeric, so only the digits survive; this is what will be stored. */
export const phoneDigits = (value: string): string => value.replace(/[^0-9]/g, '');

export const isValidPhone = (value: string): boolean => {
  const digits = phoneDigits(value);
  return digits.length >= 7 && digits.length <= 15;
};

export const staffInviteService = {
  async fetchInvite(token: string): Promise<StaffInviteDetails> {
    const { data, error } = await supabase.rpc('get_staff_invite_by_token', { p_token: token });
    if (error) throw error;

    const payload = data as {
      success?: boolean;
      reason?: string;
      invite?: Record<string, unknown>;
    } | null;

    if (!payload?.success || !payload.invite) {
      throw new StaffInviteError(toFailure(payload?.reason));
    }

    const invite = payload.invite;
    const role = toRole(invite.role);
    return {
      email: (invite.email as string) ?? '',
      first_name: (invite.first_name as string) ?? '',
      last_name: (invite.last_name as string) ?? '',
      role,
      is_guard: typeof invite.is_guard === 'boolean' ? invite.is_guard : role === 'guard',
      company_name: (invite.company_name as string | null) ?? null,
      expires_at: (invite.expires_at as string | null) ?? null,
    };
  },

  /** Sets the password and finishes the profile. The account can sign in once this resolves. */
  async acceptInvite(params: AcceptStaffInviteParams): Promise<AcceptedStaffInvite> {
    const { data, error } = await supabase.functions.invoke('accept-staff-invite', {
      body: {
        token: params.token,
        firstName: params.firstName,
        lastName: params.lastName,
        phone: params.phone,
        password: params.password,
      },
    });

    if (error) {
      const { message, reason } = await readFunctionError(error);
      if (reason) throw new StaffInviteError(toFailure(reason), message ?? undefined);
      throw new Error(message ?? 'Failed to accept invite');
    }

    const result = data as { email?: string; role?: string; isGuard?: boolean } | null;
    const role = toRole(result?.role);
    return {
      email: result?.email ?? '',
      role,
      // An accept function deployed before roles existed does not say.
      is_guard: typeof result?.isGuard === 'boolean' ? result.isGuard : role === 'guard',
    };
  },

  /** Returns false rather than throwing: the account exists either way, and the page says how to sign in. */
  async signIn(email: string, password: string): Promise<boolean> {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return !error && Boolean(data.session);
  },

  async signOut(): Promise<void> {
    await supabase.auth.signOut({ scope: 'local' });
  },

  async fetchLicenceTypes(): Promise<LicenceTypeOption[]> {
    const { data, error } = await supabase
      .from('licence_types')
      .select('id, name, authority, country_code')
      .eq('is_active', true)
      .order('authority')
      .order('name');

    if (error) throw error;
    return (data ?? []) as LicenceTypeOption[];
  },

  /**
   * Same route the guard app takes: upload the scan into the guard's own
   * folder, then submit_guard_licence puts it in the review queue. Must be
   * called while signed in as the guard.
   */
  async submitLicence(params: SubmitLicenceParams): Promise<void> {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) throw new Error('Sign in again before adding your licence');

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', userId)
      .single();

    const companyId = (profile as { company_id: string | null } | null)?.company_id;
    if (profileError || !companyId) throw new Error('Your account is not linked to a company yet');

    const extension = LICENCE_DOCUMENT_TYPES[params.document.type] ?? 'jpg';
    const storagePath = `${companyId}/${userId}/${Date.now()}_licence.${extension}`;

    const { error: uploadError } = await supabase.storage
      .from(LICENCE_DOCUMENT_BUCKET)
      .upload(storagePath, params.document, {
        upsert: false,
        contentType: params.document.type || undefined,
      });
    if (uploadError) throw uploadError;

    const { error: submitError } = await supabase.rpc('submit_guard_licence', {
      p_licence_holder_name: params.holderName,
      p_licence_number: params.licenceNumber,
      p_licence_type_id: params.licenceTypeId,
      p_issue_date: params.issueDate,
      p_expiry_date: params.expiryDate,
      p_document_storage_path: storagePath,
      p_guard_notes: params.notes,
    });

    if (submitError) {
      // Best-effort: do not leave a scan behind that no licence row points at.
      void supabase.storage.from(LICENCE_DOCUMENT_BUCKET).remove([storagePath]).catch(() => undefined);
      throw submitError;
    }
  },
};
